<template>
  <div class="attachment-manager" :class="{ disabled }">
    <label
      class="upload-zone"
      :class="{ dragging: isDragging }"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <input
        ref="fileInput"
        type="file"
        class="file-input"
        multiple
        :id="id"
        :disabled="disabled"
        @change="onFileChange"
      />
      <div class="upload-content">
        <p class="upload-title">{{ uploadTitle }}</p>
        <p class="upload-subtitle">{{ uploadSubtitle }}</p>
      </div>
    </label>

    <ul v-if="visibleAttachments.length" class="attachment-list">
      <li
        v-for="attachment in visibleAttachments"
        :key="attachment.localId || attachment.id"
        class="attachment-row"
      >
        <div class="attachment-meta">
          <span class="attachment-name">{{ attachment.name || 'Untitled file' }}</span>
          <span v-if="attachment.size" class="attachment-size">{{ formatSize(attachment.size) }}</span>
        </div>
        <div class="attachment-actions">
          <button
            type="button"
            class="text-button"
            @click="openAttachment(attachment)"
            :disabled="disabled || openingId === (attachment.id || attachment.localId)"
          >
            View
          </button>
          <button
            type="button"
            class="text-button danger"
            @click="removeAttachment(attachment)"
            :disabled="disabled"
          >
            Remove
          </button>
        </div>
      </li>
    </ul>
    <p v-else class="empty-hint">{{ emptyText }}</p>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { useMediaStore } from '@/stores/media';
import {
  createAttachmentFromFile,
  ensureAttachmentArray,
} from '@/utils/attachments';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  id: {
    type: String,
    default: '',
  },
  uploadTitle: {
    type: String,
    default: 'Upload attachments',
  },
  uploadSubtitle: {
    type: String,
    default: 'Drop files here or click to browse',
  },
  emptyText: {
    type: String,
    default: 'No attachments yet.',
  },
});

const emit = defineEmits(['update:modelValue']);

const attachments = computed({
  get: () => ensureAttachmentArray(props.modelValue),
  set: (value) => emit('update:modelValue', ensureAttachmentArray(value)),
});
const visibleAttachments = computed(() =>
  attachments.value.filter((attachment) => attachment.status !== 'delete')
);

const fileInput = ref(null);
const isDragging = ref(false);
const openingId = ref(null);

const instance = getCurrentInstance();
const toast = instance?.proxy?.$toast;
const logError = instance?.proxy?.$logError;
const mediaStore = useMediaStore();

const onFileChange = (event) => {
  if (props.disabled) return;
  const files = Array.from(event.target?.files || []);
  addFiles(files);
  if (event.target) {
    event.target.value = '';
  }
};

const onDragOver = () => {
  if (props.disabled) return;
  isDragging.value = true;
};

const onDragLeave = () => {
  isDragging.value = false;
};

const onDrop = (event) => {
  if (props.disabled) return;
  const files = Array.from(event.dataTransfer?.files || []);
  addFiles(files);
  isDragging.value = false;
};

const addFiles = (files) => {
  if (!files?.length) return;
  const current = attachments.value.slice();

  files.forEach((file) => {
    if (!file) return;
    const duplicate = current.some(
      (attachment) =>
        attachment.name === file.name &&
        attachment.size === file.size &&
        attachment.status !== 'delete'
    );
    if (duplicate) {
      toast?.info?.(`${file.name} is already attached.`);
      return;
    }
    current.push(createAttachmentFromFile(file));
  });

  attachments.value = current;
};

const removeAttachment = (attachment) => {
  if (!attachment) return;
  const current = attachments.value.slice();
  const index = current.findIndex(
    (item) => item.localId === attachment.localId || item.id === attachment.id
  );
  if (index === -1) return;

  if (!current[index]?.id) {
    current.splice(index, 1);
  } else {
    current[index] = { ...current[index], status: 'delete' };
  }

  attachments.value = current;
};

const openAttachment = async (attachment) => {
  if (!attachment || props.disabled) return;
  let url = '';
  let revokeBlobUrl = false;
  const attachmentKey = attachment.id || attachment.localId;
  openingId.value = attachmentKey;

  try {
    if (attachment.file) {
      url = URL.createObjectURL(attachment.file);
      revokeBlobUrl = true;
    } else if (attachment.id) {
      const signed = await mediaStore.getSignedUrl(attachment.id);
      url = signed?.url || '';
    }

    if (!url) {
      toast?.warn?.('No preview available for this attachment.');
      return;
    }

    window.open(url, '_blank', 'noopener');
  } catch (error) {
    toast?.error?.('Unable to open attachment.');
    logError?.(error, 'AttachmentManager::openAttachment');
  } finally {
    if (revokeBlobUrl && url) {
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 1000);
    }
    openingId.value = null;
  }
};

const formatSize = (size) => {
  if (!size && size !== 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};
</script>

<style scoped>
.attachment-manager {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.attachment-manager.disabled {
  opacity: 0.6;
  pointer-events: none;
}

.upload-zone {
  border: 1px dashed var(--border-color);
  border-radius: 8px;
  padding: 16px;
  background: var(--noc-background);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.upload-zone.dragging {
  border-color: var(--noc-blue);
  background: var(--noc-blue-10);
}

.upload-content {
  text-align: center;
}

.upload-title {
  font-weight: 600;
  margin: 0;
  color: var(--text-color);
}

.upload-subtitle {
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: var(--text-color-secondary);
}

.file-input {
  display: none;
}

.attachment-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attachment-row {
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: var(--noc-background);
}

.attachment-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.attachment-name {
  font-weight: 500;
  color: var(--text-color);
  font-size: 0.9rem;
}

.attachment-size {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}

.attachment-actions {
  display: flex;
  gap: 8px;
}

.text-button {
  border: none;
  background: transparent;
  color: var(--noc-blue);
  cursor: pointer;
  font-weight: 600;
  font-size: 0.8rem;
}

.text-button.danger {
  color: var(--noc-red);
}

.empty-hint {
  font-size: 0.85rem;
  color: var(--text-color-secondary);
  margin: 4px 0 0 0;
  font-style: italic;
  opacity: 0.7;
}
</style>
